import { useState, useEffect, useRef, useCallback } from 'react';
import Busqueda from '../components/Busqueda';
import CategoryButton from '../components/CategoryButton';
import Switch from '../Utils/Switch';
import EmptyState from '../components/EmptyState';
import { ensureMinLoading } from '../Utils/withLoading';
import { apiFetch } from '../api/client';
import TipBlue from '../Utils/TipBlue';

const FONT = '"Public Sans", sans-serif';

const days = [
  { id: 1, name: 'Lun', full: 'Lunes', key: 'mon' },
  { id: 2, name: 'Mar', full: 'Martes', key: 'tue' },
  { id: 3, name: 'Mié', full: 'Miércoles', key: 'wed' },
  { id: 4, name: 'Jue', full: 'Jueves', key: 'thu' },
  { id: 5, name: 'Vie', full: 'Viernes', key: 'fri' },
  { id: 6, name: 'Sáb', full: 'Sábado', key: 'sat' },
  { id: 7, name: 'Dom', full: 'Domingo', key: 'sun' },
];

function normalizeText(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

export default function GestionDiaria({ notify }) {
  const todayId = new Date().getDay() || 7;
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState(null);
  const firstLoad = useRef(true);

  const fetchData = useCallback(async (showLoader = false) => {
    if (showLoader) setLoading(true);
    try {
      const [cRes, pRes] = await Promise.all([
        apiFetch(`/categories`),
        apiFetch(`/products`),
      ]);
      if (!cRes.ok || !pRes.ok) throw new Error(`HTTP ${cRes.status}`);
      const cats = await cRes.json();
      const prods = await pRes.json();
      setCategories(Array.isArray(cats) ? cats : []);
      setProducts(Array.isArray(prods) ? prods : []);
      setActiveCategory((prev) => prev ?? (Array.isArray(cats) ? cats[0]?.id : null));
    } catch {
      notify?.('error', 'Algo ha salido mal. Si el problema persiste, por favor reporta el error.');
    } finally {
      if (showLoader) setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    if (firstLoad.current) {
      firstLoad.current = false;
      fetchData(true);
    }
  }, [fetchData]);

  const activeKey = days.find((d) => d.id === todayId)?.key || 'mon';

  const runWithLoading = async (loadingMsg, action) => {
    const startedAt = Date.now();
    notify?.('loading', loadingMsg);
    let ok = true;
    try {
      await action();
    } catch {
      ok = false;
    }
    await ensureMinLoading(startedAt);
    notify?.('clear');
    return ok;
  };

  const toggleCategoryDay = async (categoryId, isAvailable) => {
    const ok = await runWithLoading(
      isAvailable ? 'Activando categoría...' : 'Desactivando categoría...',
      async () => {
        const res = await apiFetch(`/categories/${categoryId}/availability-day`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ day: activeKey, is_available: isAvailable }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
      }
    );
    if (ok) {
      notify?.('success', isAvailable ? 'Categoría activada para este día' : 'Categoría desactivada para este día');
      fetchData();
    } else {
      notify?.('error', 'No se pudo actualizar la categoría');
    }
  };

  const toggleProductDay = async (productId, isAvailable) => {
    const ok = await runWithLoading(
      isAvailable ? 'Activando artículo...' : 'Desactivando artículo...',
      async () => {
        const res = await apiFetch(`/products/${productId}/availability-day`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ day: activeKey, is_available: isAvailable }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
      }
    );
    if (ok) {
      notify?.('success', isAvailable ? 'Artículo activado para este día' : 'Artículo desactivado para este día');
      fetchData();
    } else {
      notify?.('error', 'No se pudo actualizar el artículo');
    }
  };

  const normalizedQuery = normalizeText(search.trim());

  const activeCategoryObj = categories.find((c) => c.id === activeCategory);
  const categoryAvailable = activeCategoryObj
    ? (activeCategoryObj.available_days || []).includes(activeKey)
    : true;

  const filteredProducts = products.filter((p) => {
    const inCategory =
      p.category_id === activeCategory ||
      (p.categories || []).some((c) => c.id === activeCategory);
    if (!inCategory) return false;
    if (!normalizedQuery) return true;
    return [p.name, p.category_name].some((v) => normalizeText(v).includes(normalizedQuery));
  });

  return (
    <div className="flex flex-col h-screen bg-black overflow-hidden">
      <header className="shrink-0 px-4 lg:px-6 pt-5 pb-4 border-b border-white/[0.16] flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h1
            className="font-semibold m-0 truncate"
            style={{
              fontSize: '22px',
              lineHeight: '28px',
              letterSpacing: '-0.02em',
              color: 'rgba(255, 255, 255, 0.95)',
              fontFamily: FONT,
            }}
          >
            Gestión diaria
          </h1>
          <p
            className="m-0 mt-1"
            style={{
              fontSize: '13px',
              lineHeight: '18px',
              fontWeight: 500,
              color: 'rgba(255, 255, 255, 0.45)',
              fontFamily: FONT,
            }}
          >
            Operaciones del {days.find((d) => d.id === todayId)?.full || 'día'}
          </p>
        </div>
        <TipBlue openUp={false} alignRight={true}>
          Activa o desactiva categorías y artículos para el día en curso.
        </TipBlue>
      </header>

      <div className="shrink-0 px-4 lg:px-6 pt-4 pb-5">
        <div className="max-w-[560px]">
          <Busqueda
            size="lg"
            value={search}
            onChange={setSearch}
            placeholder="Buscar categoría o artículo"
          />
        </div>
      </div>

      {/* Categorías */}
      <div className="shrink-0 px-4 lg:px-6 pb-3">
        <div
          role="tablist"
          aria-label="Categorías"
          className="grid gap-2"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 148px), 1fr))' }}
        >
          {categories.map((cat) => {
            return (
              <CategoryButton
                key={cat.id}
                name={cat.name}
                icon={cat.icon}
                color={cat.color}
                fill
                active={activeCategory === cat.id}
                onClick={() => setActiveCategory(cat.id)}
              />
            );
          })}
        </div>
      </div>

      {/* Productos */}
      <div className="flex-1 min-h-0 px-4 lg:px-6 pb-4 overflow-y-auto pedidos-scroll">
        {loading ? (
          <div className="grid gap-3 content-start" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gridAutoRows: '96px' }}>
            {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
              <div key={i} className="rounded-2xl animate-pulse" style={{ backgroundColor: '#141414' }} />
            ))}
          </div>
        ) : filteredProducts.length === 0 ? (
          <EmptyState
            title="Sin artículos"
            description="No hay artículos en esta categoría para este día."
            className="h-full"
          />
        ) : (
          <div
            className="grid gap-3 content-start"
            style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gridAutoRows: '80px' }}
          >
            {filteredProducts.map((product) => {
              const availableOnDay = (product.available_days || []).includes(activeKey);
              return (
                <div
                  key={product.id}
                  className="flex items-center justify-between gap-3 min-h-[64px] px-4 py-3 rounded-2xl"
                  style={{
                    backgroundColor: categoryAvailable
                      ? 'rgba(255, 255, 255, 0.035)'
                      : 'rgba(255, 255, 255, 0.015)',
                  }}
                >
                  <div className="flex flex-col justify-center min-w-0 flex-1">
                    <span
                      className="truncate"
                      style={{
                        fontSize: '14px',
                        lineHeight: '19px',
                        fontWeight: 600,
                        color: categoryAvailable ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.35)',
                        fontFamily: FONT,
                      }}
                    >
                      {product.name}
                    </span>
                    <span
                      style={{
                        fontSize: '12px',
                        lineHeight: '16px',
                        fontWeight: 500,
                        color: 'rgba(255, 255, 255, 0.4)',
                        fontFamily: FONT,
                        marginTop: '2px',
                      }}
                    >
                      {product.sold_units ?? 0} unidades
                    </span>
                  </div>
                  <div className="shrink-0">
                    <Switch
                      showLabel={false}
                      defaultOn={availableOnDay}
                      forcedOff={!categoryAvailable}
                      disabled={!categoryAvailable}
                      onChange={(val) => toggleProductDay(product.id, val)}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Switch de categoría activa para el día */}
      {activeCategoryObj && (
        <div className="shrink-0 px-4 lg:px-6 pb-4 pt-2 border-t border-white/[0.16]">
          <div className="flex items-center justify-between gap-4 rounded-2xl px-4 py-3" style={{ backgroundColor: 'rgba(255, 255, 255, 0.035)' }}>
            <div className="flex flex-col min-w-0">
              <span
                style={{
                  fontSize: '14px',
                  lineHeight: '19px',
                  fontWeight: 600,
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontFamily: FONT,
                }}
              >
                {activeCategoryObj.name} — {days.find((d) => d.id === todayId)?.full}
              </span>
              <span
                style={{
                  fontSize: '12px',
                  lineHeight: '16px',
                  fontWeight: 500,
                  color: 'rgba(255, 255, 255, 0.4)',
                  fontFamily: FONT,
                  marginTop: '2px',
                }}
              >
                {categoryAvailable ? 'Categoría activa este día' : 'Categoría desactivada este día'}
              </span>
            </div>
            <Switch
              showLabel={false}
              defaultOn={categoryAvailable}
              onChange={(val) => toggleCategoryDay(activeCategory, val)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
